
import { useEffect } from "react";
import { useSavedResumes } from "@/hooks/useSavedResumes";
import { useResumeOperations } from "@/hooks/useResumeOperations";
import ResumeHeader from "./resume/header/ResumeHeader";
import ResumeContent from "./resume/content/ResumeContent";
import ResumeDialogs from "./resume/dialogs/ResumeDialogs";
import { Button } from "@/components/ui/button"; 

interface SavedResumeProps {
  onNewResume: () => void;
}

const SavedResumes = ({ onNewResume }: SavedResumeProps) => {
  // This hook will only fetch non-original resumes (optimized versions)
  const { data: resumes = [], isLoading, error, refetch } = useSavedResumes();
  const {
    showPreview,
    setShowPreview,
    selectedResume,
    showPayment,
    setShowPayment,
    showFormatDialog,
    setShowFormatDialog,
    selectedFormat,
    handleCreateTestResume,
    handlePreview,
    handleDownloadClick,
    handlePayment,
    handleDeleteResume,
    onFormatSelect
  } = useResumeOperations();

  useEffect(() => {
    if (!resumes.some(r => r.status === 'pending')) return;
    
    const interval = setInterval(() => {
      refetch();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [resumes, refetch]);

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
        <div className="text-lg">Loading your optimized resumes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <div className="text-red-500 mb-4">
          Error loading resumes: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ResumeHeader 
        onNewResume={onNewResume}
        onCreateTest={handleCreateTestResume}
      />
      
      <ResumeContent
        resumes={resumes}
        onCreateTest={handleCreateTestResume}
        onPreview={handlePreview}
        onDownload={handleDownloadClick}
        onDelete={handleDeleteResume}
      />

      <ResumeDialogs
        selectedResume={selectedResume}
        showPreview={showPreview}
        showPayment={showPayment}
        showFormatDialog={showFormatDialog}
        selectedFormat={selectedFormat}
        onPreviewChange={setShowPreview}
        onPaymentChange={setShowPayment}
        onFormatDialogChange={setShowFormatDialog}
        onPayment={handlePayment}
        onFormatSelect={onFormatSelect}
      />
    </div>
  );
};

export default SavedResumes;
